import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { errMsg } from "../lib/api";
import { LABEL } from "../pages/dashboard/SiteParts";

export const PAID_TYPES = ["booking", "shop", "gallery", "faq", "testimonials", "services"];
const clone = (x) => JSON.parse(JSON.stringify(x));
const newId = (type) => `${type.slice(0, 12)}-${Date.now().toString(36)}`;

export function newSection(type) {
  const base = { id: newId(type), type, title: LABEL[type] || "New section", body: "", items: [] };
  if (type === "cta") return { ...base, title: "Ready to get started?", body: "[Short invitation]", items: [{ title: "Get in touch", text: "" }] };
  if (type === "contact" || type === "booking") return { ...base, body: "[Address]\n[Phone]\n[Email]" };
  if (type === "newsletter") return { ...base, body: "[Why subscribe]" };
  return { ...base, items: [1, 2, 3].map((n) => ({ title: `[Item ${n}]`, text: "[Short description]" })) };
}

function quoteToast(q) {
  if (!q) return;
  if (q.notice) { toast.message(q.notice); return; }
  const parts = [...q.added.map((n) => `${n} added`), ...q.removed.map((n) => `${n} removed`)];
  if (parts.length) toast.success(`Quotation updated: ${parts.join(", ")}. New total ${q.currency} ${Number(q.total || 0).toLocaleString("en-IN")}.`, { duration: 7000 });
}

export function useContentEditor({ api, pid, spec, setSpec, initialUndo = 0, initialLabel = null }) {
  const [target, setTarget] = useState(null);
  const [busy, setBusy] = useState(false);
  const [undoInfo, setUndoInfo] = useState({ count: initialUndo, label: initialLabel });
  useEffect(() => { setUndoInfo((u) => (u.count ? u : { count: initialUndo, label: initialLabel })); }, [initialUndo, initialLabel]);
  const [quote, setQuote] = useState(null);
  const send = useCallback(async (body) => {
    setBusy(true);
    try {
      const r = await api.post(`/projects/${pid}/design/content`, body);
      setSpec(r.data.spec); setUndoInfo({ count: r.data.undo_count, label: r.data.undo_label });
      if (r.data.quote) { setQuote(r.data.quote); quoteToast(r.data.quote); }
      return true;
    } catch (e) { toast.error(errMsg(e)); return false; } finally { setBusy(false); }
  }, [api, pid, setSpec]);
  const order = useCallback((c) => {
    const o = spec.layout.sections_order?.length ? spec.layout.sections_order : [];
    const ids = c.sections.map((s) => s.id);
    return [...o.filter((i) => ids.includes(i)), ...ids.filter((i) => !o.includes(i))];
  }, [spec]);
  const commit = useCallback((content, label, ord) => send({ action: "save", content, sections_order: ord || order(content), label }), [send, order]);
  const ed = useMemo(() => {
    if (!spec) return null;
    const c = spec.content;
    const ord = order(c);
    return {
      open: (t) => setTarget(t),
      move: (id, dir) => {
        const o = [...ord], i = o.indexOf(id), j = i + dir;
        if (j < 0 || j >= o.length) return;
        [o[i], o[j]] = [o[j], o[i]];
        commit(c, "Move section", o);
      },
      remove: (id) => {
        if (c.sections.length <= 1) { toast.error("Your website needs at least one section."); return; }
        commit({ ...c, sections: c.sections.filter((s) => s.id !== id) }, "Remove section", ord.filter((x) => x !== id));
      },
      duplicate: (id) => {
        const s = c.sections.find((x) => x.id === id), cp = { ...clone(s), id: newId(s.type) }, o = [...ord];
        o.splice(o.indexOf(id) + 1, 0, cp.id);
        commit({ ...c, sections: [...c.sections, cp] }, "Duplicate section", o);
      },
      addAfter: (id) => setTarget({ kind: "add", after: id }),
      setText: (path, v) => {
        const required = (path[0] === "hero" && path[1] === "title") || path[0] === "site_name" || (path[0] === "s" && path[2] === "title");
        if (!v && required) { toast.error("This heading can't be empty — your previous text was kept."); setSpec({ ...spec }); return; }
        const n = clone(c);
        if (path[0] === "s") {
          const s = n.sections.find((x) => x.id === path[1]);
          if (!s) return;
          if (path.length === 3) s[path[2]] = v; else if (s.items?.[path[2]]) s.items[path[2]][path[3]] = v;
        } else if (path[0] === "hero") n.hero[path[1]] = v;
        else n[path[0]] = v;
        commit(n, "Edit text", ord);
      },
    };
  }, [spec, order, commit, setSpec]);
  const addSection = (type, after) => {
    const s = newSection(type), o = order(spec.content);
    o.splice(after ? o.indexOf(after) + 1 : o.length, 0, s.id);
    return commit({ ...spec.content, sections: [...spec.content.sections, s] }, `Add ${LABEL[type] || type}`, o).then((ok) => ok && setTarget({ kind: "section", id: s.id }));
  };
  return { ed, target, setTarget, commit, addSection, busy, undoInfo, quote,
    undo: () => send({ action: "undo" }), resetContent: () => send({ action: "reset_content" }), resetAll: () => send({ action: "reset_all" }) };
}
